import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AxiosClient from '../assets/AxiosClient';
import { useStateContext } from '../Contexts/ContextProvider';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const { setToken, setUser, setTenant } = useStateContext();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await AxiosClient.post('/login/Admin', { email, password });
      if (response.status === 200) {
        const { user, token, tenant } = response.data;
        if (!token || !user || user.role !== 'Admin') {
          throw new Error('This account is not authorized for the tenant admin panel.');
        }
        if (!tenant?.id) {
          throw new Error('This account is not connected to an active tenant. Contact your system administrator.');
        }
        if (tenant.expires_at && new Date(tenant.expires_at).getTime() < Date.now()) {
          throw new Error('This tenant subscription has expired. Contact your system administrator.');
        }
        setUser(user);
        setTenant(tenant);
        setToken(token);
        navigate('/dashboard', { replace: true });
      }
    } catch (err) {
      setError(err.response?.data?.message || err.response?.data?.error || err.message || 'Unable to sign in. Check your credentials and tenant access.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="auth-page">
      <section className="auth-card" aria-label="Velorates sign in">
        <div className="auth-card__story">
          <div className="auth-brand">
            <img className="auth-brand__logo" src="/full-logo.png" alt="Velorates" />
          </div>
          <div className="auth-story__content">
            <p className="auth-story__eyebrow">EVENT OPERATIONS PLATFORM</p>
            <h1>Every event,<br />under control.</h1>
            <p>Plan, coordinate, and measure your conferences from one clear workspace.</p>
          </div>
          <div className="auth-story__footer" aria-label="Platform capabilities">
            <span>Event planning</span>
            <span>Guest management</span>
            <span>Live insights</span>
          </div>
        </div>

        <div className="auth-card__form">
          <h2>Welcome back</h2>
          <p className="auth-card__intro">Sign in to your Velorates workspace.</p>

          <div>
            {error && (
              <div className="alert alert-danger alert-dismissible fade show text-white" role="alert">
                <span className="alert-icon"><i className="fas fa-exclamation-circle me-2"></i></span>
                {error}
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setError('')}
                  aria-label="Close"
                />
              </div>
            )}

            <form onSubmit={handleSubmit}>
              <div className="auth-field">
                <label htmlFor="login-email">Work email</label>
                <input
                  id="login-email"
                  type="email"
                  className="form-control"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoComplete="username"
                  required
                  placeholder="name@company.com"
                />
              </div>

              <div className="auth-field position-relative">
                <label htmlFor="login-password">Password</label>
                <input
                  id="login-password"
                  type={showPassword ? 'text' : 'password'}
                  className="form-control"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="current-password"
                  required
                  placeholder="Enter your password"
                />
                <button
                  type="button"
                  className="btn btn-link auth-password-toggle"
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  <i className={`fas ${showPassword ? 'fa-eye-slash' : 'fa-eye'} text-secondary`} />
                </button>
              </div>

              <button
                type="submit"
                className="btn auth-submit w-100"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                    Signing in...
                  </>
                ) : (
                  'Sign in'
                )}
              </button>
            </form>
          </div>

          <p className="auth-card__fine-print">Secure access for your event team</p>
        </div>
      </section>
    </main>
  );
};

export default Login;